import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { FINGERPRINT_HELP, shortFingerprint } from '../../lib/fingerprint.js';
import {
    MSA_AWAITING_SIGNATURE,
    partiesComplete,
    partiesFields,
    signatureMatches,
    taxFormFor,
} from '../../lib/msa.js';
import { clearDraft, loadDraft, saveDraft } from '../../lib/msa-draft.js';
import { MSA_DETAILS_STEP, MSA_SIGN_STEP } from '../../lib/onboarding.js';
import { printDocument } from '../../lib/print-document.js';
import { marked } from '../../vendor/marked.js';
import { renderOnboardingSteps } from '../agreements/onboarding-steps.js';

/** This browser's local storage, or undefined where it is blocked. */
function localStore() {
    try {
        return window.localStorage;
    } catch {
        return undefined;
    }
}

/**
 * A freelancer's master services agreement (issue #243).
 *
 * Unsigned, it is the signing flow, and the app shows it in place of
 * everything else until it is signed. It is the last two steps of a
 * freelancer's onboarding (`steps`, from the app, drives the step indicator):
 * 1. Legal name and address: the freelancer enters the parties block (legal name and
 *    address). Continuing renders the agreement with it, server-side.
 * 2. Master services agreement: the full rendered agreement is shown, and they give a
 *    separate consent to sign electronically, type their legal name as the
 *    signature, and sign. Going back to change a detail discards the rendered
 *    text, so it is shown again before it can be signed.
 *
 * The API re-renders and compares the hash of the text shown, so what is
 * stored is exactly what was on screen. Once signed, the app moves on to the
 * Agreements page, which shows the signed agreement. The markdown rendered
 * here is our own document with the parties block escaped server-side.
 */
class MSAAgreement extends BaseComponent {
    static properties = {
        steps: { type: Array, attribute: false },
        _msa: { type: Object, state: true },
        _parties: { type: Object, state: true },
        _preview: { type: Object, state: true },
        _consent: { type: Boolean, state: true },
        _typed: { type: String, state: true },
        _loading: { type: Boolean, state: true },
        _busy: { type: Boolean, state: true },
        _error: { type: String, state: true },
    };

    static styles = css`
        /* In onboarding the app gives this the window's height: on the sign
           step the agreement takes what is left after the heading, and only
           its text scrolls, so the signature and Sign button stay in view. */
        :host { display: flex; flex-direction: column; min-height: 0; }
        .msa { margin: 0; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
        .msa > * { flex: none; }
        /* A column stretches its children; buttons keep their own width. */
        .msa > .btn { align-self: flex-start; }
        .msa h4 { margin: 0 0 0.25rem; font-size: 1.5rem; font-weight: 600; }
        .intro { color: #666; font-size: 0.9rem; margin-bottom: 1.25rem; }
        .doc { border: 1px solid #e5e5e5; border-radius: 8px; margin-bottom: 1.25rem; overflow: hidden; background: #fff; }
        /* The onboarding cards match the agreement gate's: a title bar, the
           body, and a footer with the action on the right. */
        .doc-header {
            display: flex; justify-content: space-between; align-items: center; gap: 1rem;
            padding: 0.75rem 1.25rem; background: #fafafa; border-bottom: 1px solid #eee;
        }
        .doc-header .title { font-weight: 600; }
        .doc-body.form-body { max-height: none; overflow: visible; }
        .doc-actions { padding: 0.75rem 1.25rem; border-top: 1px solid #eee; text-align: right; }
        .doc-body { padding: 1rem 1.25rem; max-height: 32rem; overflow-y: auto; font-size: 0.9rem; line-height: 1.55; }
        .doc-body h1 { font-size: 1.25rem; margin-top: 0; text-align: center; }
        .doc-body h2 { font-size: 1.05rem; margin-top: 1.25rem; }
        .msa > .doc.full { flex: 1 1 auto; min-height: 12rem; display: flex; flex-direction: column; }
        .doc.full .doc-body { flex: 1 1 auto; min-height: 0; max-height: none; }
        .doc-footer { padding: 0.5rem 1rem; border-top: 1px solid #eee; font-size: 0.75rem; color: #888; word-break: break-all; }
        .sign-box { border: 1px solid #cfe2ff; background: #f5f9ff; border-radius: 8px; padding: 1rem 1.25rem; }
        .consent { font-size: 0.88rem; }
        .agree-note { font-size: 0.85rem; color: #555; }
    `;

    constructor() {
        super();
        /** @type {Array<{ key: string, label: string }>} */
        this.steps = [];
        this._msa = null;
        this._parties = { legal_name: '', street: '', city: '', region: '', postal_code: '' };
        this._preview = null;
        this._consent = false;
        this._typed = '';
        this._loading = true;
        this._busy = false;
        this._error = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            this._msa = await api.get('/api/msa');
            await this._resumeSavedDetails();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    // Details saved before a reload put the freelancer back on the signing
    // step with them filled in. A failed render just leaves them on the
    // details step, with what they saved.
    async _resumeSavedDetails() {
        if (this._msa?.status !== MSA_AWAITING_SIGNATURE) return;
        const saved = loadDraft(localStore(), this._msa.contract_id);
        if (!saved) return;
        this._parties = saved;
        if (!partiesComplete(saved)) return;
        try {
            this._preview = await api.post('/api/msa/preview', saved);
        } catch {
            this._preview = null;
        }
    }

    _setParty(key, value) {
        this._parties = { ...this._parties, [key]: value };
    }

    // Back to the details step. The rendered text is dropped: changed details
    // are a different agreement, which must be shown again before signing.
    _editDetails() {
        this._preview = null;
        this._error = null;
        window.scrollTo(0, 0);
    }

    // Saves the details in this browser (see msa-draft.js) and renders the
    // agreement with them for the signing step.
    async _save() {
        this._error = null;
        this._busy = true;
        saveDraft(localStore(), this._msa.contract_id, this._parties);
        try {
            this._preview = await api.post('/api/msa/preview', this._parties);
            this._consent = false;
            this._typed = '';
            window.scrollTo(0, 0);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._busy = false;
        }
    }

    async _sign() {
        this._error = null;
        this._busy = true;
        try {
            await api.post('/api/msa/sign', {
                ...this._parties,
                document_sha256: this._preview.document_sha256,
                consent: this._consent,
                typed_signature: this._typed,
            });
        } catch (err) {
            this._error = err.message;
            this._busy = false;
            return;
        }
        this._busy = false;
        clearDraft(localStore(), this._msa.contract_id);
        this.dispatchEvent(new CustomEvent('msa-signed', { bubbles: true, composed: true }));
    }

    render() {
        if (this._loading) {
            return html`<div class="msa text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>`;
        }
        const status = this._msa?.status;
        return html`
            <div class="msa">
                ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
                ${status === MSA_AWAITING_SIGNATURE ? this._renderSigning() : ''}
            </div>
        `;
    }

    _renderSigning() {
        return this._preview ? this._renderSignStep() : this._renderDetailsStep();
    }

    // The intro over both MSA steps, under the same heading and above the step
    // indicator as the agreement gate's, so the page doesn't shift as a
    // freelancer moves from the documents to the agreement.
    _renderOnboardingHeader(intro) {
        return html`
            <h4>Before you get started</h4>
            <p class="intro">${intro}</p>
        `;
    }

    _renderDetailsStep() {
        const doc = this._msa.document;
        return html`
            ${this._renderOnboardingHeader(
                'Your Master Services Agreement with Dice Driven Design LLC sets the terms for all the work you do for us. Each piece of work is then agreed in its own Statement of Work.',
            )}
            ${renderOnboardingSteps(this.steps, MSA_DETAILS_STEP)}
            <div class="doc">
                <div class="doc-header"><span class="title">Legal Name and Address</span></div>
                <div class="doc-body form-body">
                    <p class="text-muted">
                        Use your full legal name as it appears on your government ID, such as a passport or
                        driver's license, and on the tax form you give us${taxFormFor(doc.lane) ? ` (your ${taxFormFor(doc.lane)})` : ''}:
                        the name you file taxes under. Your address is where you live. Both go into your
                        agreement, and your legal name is the name every Statement of Work is made under.
                    </p>
                    <div class="row g-2">
                        ${partiesFields(doc).map(
                            (f) => html`
                            <div class="${f.key === 'legal_name' || f.key === 'street' ? 'col-12' : 'col-md-4'}">
                                <label class="form-label mb-1" for="msa-${f.key}">${f.label}</label>
                                <input id="msa-${f.key}" class="form-control" maxlength="200"
                                       .value="${this._parties[f.key]}"
                                       @input="${(e) => this._setParty(f.key, e.target.value)}">
                            </div>
                        `,
                        )}
                        <div class="col-12 text-muted">Country: ${doc.country}</div>
                    </div>
                </div>
                <div class="doc-actions">
                    <button class="btn btn-primary" ?disabled="${!partiesComplete(this._parties) || this._busy}"
                            @click="${this._save}">
                        ${this._busy ? 'Saving…' : 'Save Legal Name and Address'}
                    </button>
                </div>
            </div>
        `;
    }

    _renderSignStep() {
        const doc = this._msa.document;
        const canSign =
            this._consent && signatureMatches(this._typed, this._parties.legal_name) && !this._busy;
        return html`
            ${this._renderOnboardingHeader(
                'Read your agreement, then sign it below. Dice Driven countersigns it, and you can then be offered work.',
            )}
            ${renderOnboardingSteps(this.steps, MSA_SIGN_STEP)}
            <div class="doc full">
                <div class="doc-header">
                    <span class="title">Master Services Agreement</span>
                    <span class="d-flex align-items-center gap-3">
                        <button class="btn btn-link btn-sm p-0" @click="${this._editDetails}">Change your name or address</button>
                        <button class="btn btn-outline-secondary btn-sm" @click="${this._printUnsigned}">
                            <i class="bi bi-printer"></i> Print
                        </button>
                    </span>
                </div>
                <div class="doc-body" .innerHTML="${marked.parse(this._preview.rendered_text)}"></div>
                <div class="doc-footer">Version ${doc.version} · <span title="${FINGERPRINT_HELP}">Document fingerprint ${shortFingerprint(this._preview.document_sha256)}</span></div>
            </div>
            <div class="sign-box">
                <div class="form-check consent mb-3">
                    <input class="form-check-input" type="checkbox" id="msa-consent" .checked="${this._consent}"
                           @change="${(e) => (this._consent = e.target.checked)}">
                    <label class="form-check-label" for="msa-consent" .innerHTML="${marked.parseInline(this._msa.consent.text)}"></label>
                </div>
                <label class="form-label mb-1" for="msa-typed">Type your full legal name to sign</label>
                <input id="msa-typed" class="form-control mb-3" placeholder="${this._parties.legal_name}"
                       .value="${this._typed}" @input="${(e) => (this._typed = e.target.value)}">
                <div class="d-flex align-items-center gap-3 flex-wrap">
                    <button class="btn btn-primary" ?disabled="${!canSign}" @click="${this._sign}">
                        ${this._busy ? 'Signing…' : 'Sign'}
                    </button>
                    <span class="agree-note">By signing, you agree to this Master Services Agreement.</span>
                </div>
            </div>
        `;
    }

    // The agreement as it will be signed, with the freelancer's details in it.
    _printUnsigned() {
        this._error = null;
        try {
            printDocument({
                title: 'Master Services Agreement',
                details: [
                    `Version ${this._msa.document.version} · not yet signed`,
                    `Document fingerprint: ${shortFingerprint(this._preview.document_sha256)}`,
                ],
                bodyHTML: marked.parse(this._preview.rendered_text),
            });
        } catch (err) {
            this._error = err.message;
        }
    }
}

customElements.define('msa-agreement', MSAAgreement);
